import { useTranslation } from 'react-i18next'
import {
  DISPLAY_GRADES,
  hasDeclaredParts,
  mergeBatteryIntoParts,
  normalizePartsAuthenticity,
  partTone,
  visiblePartIds,
  type PartId,
  type PartStatus,
  type PartsAuthenticityMap,
} from '@redevicer/shared'

type Props = {
  parts?: PartsAuthenticityMap | null
  batteryOriginal?: boolean | null
  manufacturer?: string | null
  modelName?: string | null
  /** Always list parts, including undeclared — transparency over a blank card. */
  alwaysShow?: boolean
  showKicker?: boolean
}

export default function PartsAuthenticityPanel({
  parts,
  batteryOriginal,
  manufacturer,
  modelName,
  alwaysShow = true,
  showKicker = true,
}: Props) {
  const { t } = useTranslation()
  const merged = mergeBatteryIntoParts(normalizePartsAuthenticity(parts), batteryOriginal)
  const ids = visiblePartIds(manufacturer, modelName, merged)
  if (!alwaysShow && !hasDeclaredParts(merged)) return null

  return (
    <section className="parts-auth" aria-label={t('partsAuth.title')}>
      <header className="parts-auth__head">
        {showKicker ? (
          <span className="parts-auth__kicker seller-declared-kicker">{t('partsAuth.kicker')}</span>
        ) : null}
        <h3 className="parts-auth__title">{t('partsAuth.title')}</h3>
        <p className="parts-auth__lead">{t('partsAuth.lead')}</p>
      </header>
      <ul className="parts-auth__list">
        {ids.map((id) => (
          <PartRow key={id} id={id} entry={merged[id]} t={t} />
        ))}
      </ul>
    </section>
  )
}

function PartRow({
  id,
  entry,
  t,
}: {
  id: PartId
  entry: PartsAuthenticityMap[PartId]
  t: (key: string, opts?: Record<string, string>) => string
}) {
  const status: PartStatus = entry?.status ?? 'unknown'
  const tone = partTone(status)
  const grade = entry?.grade
  const statusLabel =
    status === 'replaced_aftermarket' && id === 'display' && grade
      ? t(`partsAuth.displayGrade.${grade}`, {
          defaultValue: t('partsAuth.status.replaced_aftermarket'),
        })
      : t(`partsAuth.status.${status}`)

  return (
    <li className={`parts-auth__row parts-auth__row--${tone}`}>
      <span className={`parts-auth__dot parts-auth__dot--${tone}`} aria-hidden="true" />
      <div className="parts-auth__copy">
        <div className="parts-auth__line">
          <span className="parts-auth__part">{t(`partsAuth.part.${id}`)}</span>
          <span className="parts-auth__status">{statusLabel}</span>
        </div>
        {status !== 'unknown' ? (
          <p className="parts-auth__explain">
            {status === 'replaced_aftermarket' && id === 'display' && grade
              ? t(`partsAuth.explain.display.${grade}`)
              : t(`partsAuth.explain.${status}`)}
          </p>
        ) : (
          <p className="parts-auth__explain">{t('partsAuth.explain.unknown')}</p>
        )}
      </div>
    </li>
  )
}
